import { useState } from 'react';
import { api, useMutation } from '../api';
import type { MeView, Enroll2faStartView } from '../api';
import { useT, type Dict } from '../i18n';
import { TotpQr } from './TotpQr';
import { ErrorState } from './feedback';

/**
 * Self-service two-factor (TOTP) card: enable (QR + recovery codes + confirm) and
 * disable (current code). Shared by the agent and admin Settings screens. 2FA is
 * optional for every role except where policy makes it mandatory server-side (a
 * disable attempt there is rejected by the API and surfaced as an error here).
 */
const dict: Dict = {
  en: {
    twoFactorHeading: 'Two-factor authentication',
    totpApp: 'Authenticator app (TOTP)',
    totpEnabled: 'enabled',
    totpNotEnrolled: 'not enrolled',
    twoFactorOptionalNotice:
      'Two-factor is optional for your account. Enable it for extra security.',
    twoFactorEnableBtn: 'Enable 2FA',
    twoFactorStarting: 'Starting…',
    twoFactorDisableBtn: 'Disable 2FA',
    twoFactorScanQr: 'Scan this QR in your authenticator app',
    twoFactorEnterManually: 'or enter this key manually',
    twoFactorRecoveryCodes: 'Recovery codes — store these now',
    twoFactorCodeLabel: '6-digit code',
    twoFactorConfirmEnable: 'Confirm & enable →',
    twoFactorConfirming: 'Confirming…',
    twoFactorCancel: 'Cancel',
    twoFactorDisableHint: 'Enter a current 6-digit code to confirm disabling 2FA.',
    twoFactorConfirmDisable: 'Disable 2FA',
    twoFactorDisabling: 'Disabling…',
    twoFactorEnabledOk: 'Two-factor authentication enabled.',
    twoFactorDisabledOk: 'Two-factor authentication disabled.',
  },
  es: {
    twoFactorHeading: 'Autenticación de doble factor',
    totpApp: 'Aplicación de autenticación (TOTP)',
    totpEnabled: 'Activado',
    totpNotEnrolled: 'no inscrito',
    twoFactorOptionalNotice:
      'El doble factor es opcional para tu cuenta. Actívalo para mayor seguridad.',
    twoFactorEnableBtn: 'Activar 2FA',
    twoFactorStarting: 'Iniciando…',
    twoFactorDisableBtn: 'Desactivar 2FA',
    twoFactorScanQr: 'Escanea este QR en tu app de autenticación',
    twoFactorEnterManually: 'o introduce esta clave manualmente',
    twoFactorRecoveryCodes: 'Códigos de recuperación — guárdalos ahora',
    twoFactorCodeLabel: 'Código de 6 dígitos',
    twoFactorConfirmEnable: 'Confirmar y activar →',
    twoFactorConfirming: 'Confirmando…',
    twoFactorCancel: 'Cancelar',
    twoFactorDisableHint:
      'Introduce un código actual de 6 dígitos para confirmar la desactivación del 2FA.',
    twoFactorConfirmDisable: 'Desactivar 2FA',
    twoFactorDisabling: 'Desactivando…',
    twoFactorEnabledOk: 'Autenticación de doble factor activada.',
    twoFactorDisabledOk: 'Autenticación de doble factor desactivada.',
  },
  de: {
    twoFactorHeading: 'Zwei-Faktor-Authentifizierung',
    totpApp: 'Authenticator-App (TOTP)',
    totpEnabled: 'aktiviert',
    totpNotEnrolled: 'nicht eingerichtet',
    twoFactorOptionalNotice:
      'Die Zwei-Faktor-Authentifizierung ist für Ihr Konto optional. Aktivieren Sie sie für zusätzliche Sicherheit.',
    twoFactorEnableBtn: '2FA aktivieren',
    twoFactorStarting: 'Wird gestartet…',
    twoFactorDisableBtn: '2FA deaktivieren',
    twoFactorScanQr: 'Scannen Sie diesen QR-Code in Ihrer Authenticator-App',
    twoFactorEnterManually: 'oder geben Sie diesen Schlüssel manuell ein',
    twoFactorRecoveryCodes: 'Wiederherstellungscodes — bewahren Sie sie jetzt auf',
    twoFactorCodeLabel: '6-stelliger Code',
    twoFactorConfirmEnable: 'Bestätigen & aktivieren →',
    twoFactorConfirming: 'Wird bestätigt…',
    twoFactorCancel: 'Abbrechen',
    twoFactorDisableHint:
      'Geben Sie einen aktuellen 6-stelligen Code ein, um die Deaktivierung von 2FA zu bestätigen.',
    twoFactorConfirmDisable: '2FA deaktivieren',
    twoFactorDisabling: 'Wird deaktiviert…',
    twoFactorEnabledOk: 'Zwei-Faktor-Authentifizierung aktiviert.',
    twoFactorDisabledOk: 'Zwei-Faktor-Authentifizierung deaktiviert.',
  },
};

export function TwoFactorCard({ me, onChanged }: { me: MeView; onChanged: () => void }) {
  const t = useT(dict);
  const enabled = me.totpEnabled;

  const [mode, setMode] = useState<'idle' | 'enrolling' | 'disabling'>('idle');
  const [enrollment, setEnrollment] = useState<Enroll2faStartView | null>(null);
  const [code, setCode] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const start = useMutation(api.auth.setup2faStart);
  const confirm = useMutation(api.auth.setup2faConfirm);
  const disable = useMutation(api.auth.disable2fa);
  const busy = start.loading || confirm.loading || disable.loading;
  const actionError = start.error ?? confirm.error ?? disable.error;

  async function beginEnable() {
    setNotice(null);
    setCode('');
    try {
      const res = await start.mutate();
      setEnrollment(res);
      setMode('enrolling');
    } catch {
      /* surfaced below */
    }
  }

  async function confirmEnable() {
    setNotice(null);
    try {
      await confirm.mutate(code);
      setMode('idle');
      setEnrollment(null);
      setCode('');
      setNotice(t('twoFactorEnabledOk'));
      onChanged();
    } catch {
      /* surfaced below */
    }
  }

  async function confirmDisable() {
    setNotice(null);
    try {
      await disable.mutate(code);
      setMode('idle');
      setCode('');
      setNotice(t('twoFactorDisabledOk'));
      onChanged();
    } catch {
      /* surfaced below */
    }
  }

  function cancel() {
    setMode('idle');
    setEnrollment(null);
    setCode('');
    setNotice(null);
  }

  return (
    <div className="panel" style={{ overflow: 'hidden' }}>
      <div className="row between center" style={{ padding: '12px 16px', borderBottom: '1px solid var(--line-2)' }}>
        <div className="h3">{t('twoFactorHeading')}</div>
      </div>
      <div className="col gap-10" style={{ padding: 16 }}>
        <div className="row between center">
          <span style={{ fontSize: 14 }}>{t('totpApp')}</span>
          <span className={`pill ${enabled ? 'green dot' : 'amber dot'}`}>
            {enabled ? t('totpEnabled') : t('totpNotEnrolled')}
          </span>
        </div>
        <div className="meta" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {t('twoFactorOptionalNotice')}
        </div>

        {mode === 'idle' && (
          <div className="row gap-6">
            {!enabled ? (
              <button type="button" className="btn primary" disabled={busy} onClick={beginEnable}>
                {start.loading ? t('twoFactorStarting') : t('twoFactorEnableBtn')}
              </button>
            ) : (
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => {
                  setMode('disabling');
                  setCode('');
                  setNotice(null);
                }}
              >
                {t('twoFactorDisableBtn')}
              </button>
            )}
          </div>
        )}

        {mode === 'enrolling' && enrollment && (
          <div className="col gap-10">
            <div className="panel" style={{ padding: 12 }}>
              <div className="label">{t('twoFactorScanQr')}</div>
              <TotpQr otpauthUrl={enrollment.otpauthUrl} />
              <div className="meta mt-8" style={{ textAlign: 'center' }}>
                {t('twoFactorEnterManually')}
              </div>
              <div
                className="mono"
                style={{ wordBreak: 'break-all', fontSize: 13, marginTop: 4, textAlign: 'center' }}
              >
                {enrollment.secret}
              </div>
            </div>
            <div className="panel" style={{ padding: 12, background: 'var(--amber-soft)' }}>
              <div className="label">{t('twoFactorRecoveryCodes')}</div>
              <div className="mono" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.7 }}>
                {enrollment.recoveryCodes.map((c) => (
                  <div key={c}>{c}</div>
                ))}
              </div>
            </div>
            <label className="col gap-4">
              <span className="label">{t('twoFactorCodeLabel')}</span>
              <input
                className="input mono"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                style={{ letterSpacing: '0.3em' }}
              />
            </label>
            <div className="row gap-6">
              <button type="button" className="btn" disabled={busy} onClick={cancel}>
                {t('twoFactorCancel')}
              </button>
              <button
                type="button"
                className="btn primary"
                style={{ flex: 1, justifyContent: 'center' }}
                disabled={busy || code.length !== 6}
                onClick={confirmEnable}
              >
                {confirm.loading ? t('twoFactorConfirming') : t('twoFactorConfirmEnable')}
              </button>
            </div>
          </div>
        )}

        {mode === 'disabling' && (
          <div className="col gap-10">
            <div className="meta">{t('twoFactorDisableHint')}</div>
            <label className="col gap-4">
              <span className="label">{t('twoFactorCodeLabel')}</span>
              <input
                className="input mono"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                style={{ letterSpacing: '0.3em' }}
              />
            </label>
            <div className="row gap-6">
              <button type="button" className="btn" disabled={busy} onClick={cancel}>
                {t('twoFactorCancel')}
              </button>
              <button
                type="button"
                className="btn red"
                style={{ flex: 1, justifyContent: 'center' }}
                disabled={busy || code.length !== 6}
                onClick={confirmDisable}
              >
                {disable.loading ? t('twoFactorDisabling') : t('twoFactorConfirmDisable')}
              </button>
            </div>
          </div>
        )}

        {notice && (
          <div className="meta" style={{ color: 'var(--green)', fontSize: 13 }}>
            {notice}
          </div>
        )}
        {actionError && <ErrorState message={actionError.message} />}
      </div>
    </div>
  );
}
